import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpErrorResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, BehaviorSubject, fromEvent } from 'rxjs';
import { catchError, retryWhen, switchMap, finalize, filter, take, mapTo, delay, concatMap } from 'rxjs/operators';


import { TranslateService } from '@ngx-translate/core';
// import { UiService } from '@services/ui.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../authentication/auth.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    private isRefreshingToken: boolean = false;
    private tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

    private isOfflineMsg = "Vous n'êtes pas connecté à internet.";

    constructor(
        private authSrv: AuthService,
        private snackbar: MatSnackBar,
        private router: Router,
        private translateService: TranslateService,
        // private uiService: UiService
    ) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            retryWhen(errors => errors
                .pipe(
                    concatMap((error, count) => {
                        if (count < 5 && (error.status === 400 || error.status === 0)) {
                            return throwError(error);
                        }
                        return throwError(error);
                    }),
                    delay(1000)
                )
            ),
            catchError(err => {
                if (err instanceof HttpErrorResponse) {
                    if (!this.isOnline) {
                        this.showMessage(this.isOfflineMsg);
                        return throwError(err);
                    }
                    else if (err.status === 401) {
                        // token outdated
                        return this.refreshToken(req, next);
                    }

                    this.handleErrorMessage(err);
                }
                return throwError(err);
            })
        );
    }

    private refreshToken(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;

            // Reset here so that the following requests wait until the token
            // comes back from the refreshToken call.
            this.tokenSubject.next('');

            return this.authSrv.refreshToken()
                .pipe(
                    switchMap(newToken => {
                        if (newToken) {
                            this.tokenSubject.next(newToken.access_token);
                            return next.handle(this.addToken(req, newToken.access_token));
                        }

                        // If we don't get a new token, we are in trouble so logout.
                        this.authSrv.logout();
                        this.translateService.get('not-authorize').subscribe((text: string) => {
                            this.showMessage(text);
                        });
                        return throwError('');
                    }),
                    catchError(error => {
                        // If there is an exception calling 'refreshToken', bad news so logout.
                        if (error.status === 401) {
                            // this.uiService.showSplashScreen(false);
                            // this.uiService.clearAllRouteLoading();
                            this.authSrv.logout();
                        }
                        // this.uiService.showSplashScreen(false);
                        // this.uiService.clearAllRouteLoading();
                        return throwError(error);
                    }),
                    finalize(() => this.isRefreshingToken = false)
                );
        } else {
            return this.tokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(token => {
                    return next.handle(this.addToken(req, token));
                })
            );
        }
    }

    private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
        return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
    }

    private handleErrorMessage(err: HttpErrorResponse): void {
        console.error(err);
        const defaultText = `${err.status} ${err.error}`;
        switch (err.status) {
            case 400: // Bad Request
                this.handle400Error(err);
                break;
            case 401: // Unauthorized
                this.router.navigate(['/login']);
                break;
            case 403: // Forbidden
                this.handle403Error(err);
                break;
            case 404: // NotFound
                this.handle404Error(err);
                break;
            case 500:
                this.handle500Error(err);
                break;
            default:
                this.showMessage('common.unknow');
        }
    }

    private handle400Error(error: HttpErrorResponse): void {
        if (typeof error.error == "string")
            this.showMessage(error.error);
        else if (error.error.requestInfo)
            this.showMessage(error.error.requestInfo.error);
        else if (typeof error.error.error == "string")
            this.showMessage(error.error.error);

    }
    private handle403Error(error: HttpErrorResponse): void {
        this.showMessage(error.error);
    }
    private handle404Error(error: HttpErrorResponse): void {
        if (typeof error.error == "string")
            this.showMessage(error.error);
        else if (error.error.requestInfo)
            this.showMessage(error.error.requestInfo.error);
        else if (typeof error.error.error == "string")
            this.showMessage(error.error.error);
    }
    private handle500Error(error: HttpErrorResponse): void {
        if (typeof error.error == "string")
            this.showMessage(error.error);
        else if (error.error.requestInfo)
            this.showMessage(error.error.requestInfo.error);
        else if (typeof error.error.error == "string")
            this.showMessage(error.error.error);
    }

    private showMessage(errorMessage: string): void {
        const error = 'errormessage.' + errorMessage
            .toLowerCase()
            .trim()
            .split(" ")
            .join("_");
        this.translateService.get(error).subscribe((text: string) => {
            console.error(error);
            this.snackbar.open(text, '', { duration: 5000 });
        });
    }

    get isOnline() {
        return navigator.onLine;
    }

}
